import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// Check if WebSocket is available in this environment
const isWebSocketAvailable = typeof WebSocket !== 'undefined';

// Define WebSocket constants for environments where WebSocket is not available
const WS_CONSTANTS = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

// Use native WebSocket or fallback constants
const WebSocketClass = isWebSocketAvailable ? WebSocket : {
  CONNECTING: WS_CONSTANTS.CONNECTING,
  OPEN: WS_CONSTANTS.OPEN,
  CLOSING: WS_CONSTANTS.CLOSING,
  CLOSED: WS_CONSTANTS.CLOSED
};

// Get WebSocket URL from API base URL
const getWebSocketUrl = (baseUrl) => {
  return baseUrl.replace(/^http/, 'ws');
};

// WebSocket URL
const WS_BASE_URL = getWebSocketUrl(API_BASE_URL);

/**
 * A robust WebSocket-based chat hook
 */
const useWebSocketChat = (streamId, user) => {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserTyping, setIsUserTyping] = useState(false); // Track if user is typing

  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const lastActivityRef = useRef(Date.now());

  const MAX_RECONNECT_ATTEMPTS = 10;
  const RECONNECT_DELAY = 2000;
  const HEARTBEAT_INTERVAL = 30000;
  const CONNECTION_TIMEOUT = 10000;

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    // Check if WebSocket is available in this environment
    if (!isWebSocketAvailable) {
      console.error('[useWebSocketChat] WebSocket is not available in this environment');
      setError('Chat is not available in this browser. Please try a different browser.');
      setIsConnected(false);
      return;
    }

    try {
      // Close existing connection if any
      if (socketRef.current && socketRef.current.readyState !== WebSocketClass.CLOSED) {
        socketRef.current.close();
      }

      // Create WebSocket URL
      const wsUrl = `${WS_BASE_URL}/stream/${streamId}`;
      console.log(`[useWebSocketChat] Connecting to WebSocket: ${wsUrl}`);

      // Create new WebSocket
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      // Set connection timeout
      const connectionTimeoutId = setTimeout(() => {
        if (ws.readyState !== WebSocketClass.OPEN) {
          console.log('[useWebSocketChat] Connection timeout, closing socket');
          ws.close();
        }
      }, CONNECTION_TIMEOUT);

      // Handle WebSocket open
      ws.onopen = () => {
        console.log('[useWebSocketChat] WebSocket connected');
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        lastActivityRef.current = Date.now();

        // Clear connection timeout
        clearTimeout(connectionTimeoutId);

        // Join stream
        if (user) {
          const joinMessage = {
            type: 'join_stream_view',
            streamId: streamId,
            userId: user._id || user.userId || user.address || `user_${Date.now()}`,
            username: user.username || (user.address ? `${user.address.slice(0, 6)}...${user.address.slice(-4)}` : `User_${Date.now().toString().slice(-4)}`),
            address: user.address
          };

          try {
            ws.send(JSON.stringify(joinMessage));
            console.log('[useWebSocketChat] Sent join message:', joinMessage);
          } catch (err) {
            console.error('[useWebSocketChat] Error sending join message:', err);
          }
        }

        // Process any queued messages
        processMessageQueue();

        // Set up heartbeat interval
        const heartbeatIntervalId = setInterval(() => {
          if (ws.readyState === WebSocketClass.OPEN) {
            try {
              ws.send(JSON.stringify({
                type: 'heartbeat',
                streamId: streamId,
                timestamp: Date.now()
              }));
              console.log('[useWebSocketChat] Sent heartbeat');
            } catch (err) {
              console.error('[useWebSocketChat] Error sending heartbeat:', err);
            }
          }
        }, HEARTBEAT_INTERVAL);

        // Store interval ID for cleanup
        ws.heartbeatIntervalId = heartbeatIntervalId;
      };

      // Handle WebSocket messages
      ws.onmessage = (event) => {
        try {
          lastActivityRef.current = Date.now();

          const data = JSON.parse(event.data);
          console.log('[useWebSocketChat] Received message:', data);

          if (data.type === 'chat') {
            // Skip updating messages if user is currently typing
            // This prevents the chat from flickering while the user is typing
            if (isUserTyping) {
              console.log('[useWebSocketChat] User is typing, deferring message update');

              // Store the message to be added later when the user is done typing
              messageQueueRef.current.push({
                type: 'deferred_message',
                data: {
                  id: data.id || data._id || Date.now(),
                  _id: data._id || data.id,
                  message: data.message,
                  address: data.address,
                  username: data.username,
                  timestamp: data.timestamp || new Date().toISOString(),
                  clientMessageId: data.clientMessageId
                }
              });

              return;
            }

            // Add message to state, but only if it doesn't already exist
            setMessages(prev => {
              // Check if we already have this message
              const isDuplicate = prev.some(msg =>
                (data.id && msg.id === data.id) ||
                (data._id && msg._id === data._id) ||
                (data.clientMessageId && msg.clientMessageId === data.clientMessageId) ||
                (msg.message === data.message &&
                 msg.username === data.username &&
                 Math.abs(new Date(msg.timestamp) - new Date(data.timestamp)) < 5000)
              );

              if (isDuplicate) {
                console.log('[useWebSocketChat] Duplicate message, not adding');
                return prev;
              }

              console.log('[useWebSocketChat] Adding new message to state');
              return [...prev, {
                id: data.id || data._id || Date.now(),
                _id: data._id || data.id,
                message: data.message,
                address: data.address,
                username: data.username,
                timestamp: data.timestamp || new Date().toISOString(),
                clientMessageId: data.clientMessageId
              }];
            });
          } else if (data.type === 'pong' || data.type === 'heartbeat_response') {
            console.log('[useWebSocketChat] Received heartbeat response');
          }
        } catch (err) {
          console.error('[useWebSocketChat] Error processing message:', err);
        }
      };

      // Handle WebSocket errors
      ws.onerror = (error) => {
        console.error('[useWebSocketChat] WebSocket error:', error);
        setError('Chat connection error. Trying to reconnect...');
      };

      // Handle WebSocket close
      ws.onclose = (event) => {
        console.log(`[useWebSocketChat] WebSocket closed with code ${event.code}, reason: ${event.reason || 'No reason provided'}`);
        setIsConnected(false);

        // Clear connection timeout
        clearTimeout(connectionTimeoutId);

        // Clear heartbeat interval
        if (ws.heartbeatIntervalId) {
          clearInterval(ws.heartbeatIntervalId);
        }

        // Try to reconnect
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;

          console.log(`[useWebSocketChat] Reconnecting in ${RECONNECT_DELAY}ms (attempt ${reconnectAttemptsRef.current})`);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, RECONNECT_DELAY);
        } else {
          setError('Unable to connect to chat. Please try refreshing the page.');
        }
      };
    } catch (err) {
      console.error('[useWebSocketChat] Error establishing WebSocket connection:', err);
      setError('Failed to connect to chat. Please try refreshing the page.');
    }
  }, [streamId, user]);

  // Process message queue
  const processMessageQueue = useCallback(() => {
    if (messageQueueRef.current.length === 0 || !socketRef.current || socketRef.current.readyState !== WebSocketClass.OPEN) {
      return;
    }

    const message = messageQueueRef.current[0];

    try {
      socketRef.current.send(JSON.stringify(message));
      console.log('[useWebSocketChat] Sent queued message:', message);

      // Remove from queue
      messageQueueRef.current.shift();

      // Process next message
      if (messageQueueRef.current.length > 0) {
        processMessageQueue();
      }
    } catch (err) {
      console.error('[useWebSocketChat] Error sending queued message:', err);

      // Increment retry count
      message.retries = (message.retries || 0) + 1;

      // Remove from queue if too many retries
      if (message.retries >= 3) {
        console.error('[useWebSocketChat] Too many retries, removing message from queue');
        messageQueueRef.current.shift();

        // Update message in state to show error
        if (message.clientMessageId) {
          setMessages(prev => prev.map(msg =>
            msg.clientMessageId === message.clientMessageId
              ? { ...msg, error: true, errorMessage: 'Failed to send' }
              : msg
          ));
        }
      }
    }
  }, []);

  // Send a message
  const sendMessage = useCallback((messageText) => {
    if (!streamId || !user) {
      console.error('[useWebSocketChat] Cannot send message: streamId or user is missing');
      return false;
    }

    if (!messageText.trim()) {
      return false;
    }

    // Mark that the user is done typing
    setIsUserTyping(false);

    // Process any deferred messages now
    const deferredMessages = messageQueueRef.current.filter(msg => msg.type === 'deferred_message');
    if (deferredMessages.length > 0) {
      console.log(`[useWebSocketChat] Processing ${deferredMessages.length} deferred messages after sending`);

      // Add all deferred messages to state
      setMessages(prev => {
        const newMessages = [...prev];

        for (const deferredMsg of deferredMessages) {
          // Check if we already have this message
          const isDuplicate = newMessages.some(msg =>
            (deferredMsg.data.id && msg.id === deferredMsg.data.id) ||
            (deferredMsg.data._id && msg._id === deferredMsg.data._id) ||
            (deferredMsg.data.clientMessageId && msg.clientMessageId === deferredMsg.data.clientMessageId)
          );

          if (!isDuplicate) {
            newMessages.push(deferredMsg.data);
          }
        }

        return newMessages;
      });

      // Remove processed messages from queue
      messageQueueRef.current = messageQueueRef.current.filter(msg => msg.type !== 'deferred_message');
    }

    try {
      // Generate a unique client message ID
      const clientMessageId = `${user.address || user.userId || 'user'}_${Date.now()}`;

      // Create message data
      const messageData = {
        type: 'chat',
        streamId,
        userId: user._id || user.userId || user.address || `user_${Date.now()}`,
        username: user.username || (user.address ? `${user.address.slice(0, 6)}...${user.address.slice(-4)}` : `User_${Date.now().toString().slice(-4)}`),
        message: messageText,
        timestamp: new Date().toISOString(),
        clientMessageId,
        address: user.address,
        broadcast: true
      };

      console.log('[useWebSocketChat] Sending message:', messageData);

      // Add message to state immediately (optimistic update)
      const optimisticMessage = {
        ...messageData,
        id: clientMessageId,
        isOptimistic: true
      };

      setMessages(prev => {
        // Check if we already have this message
        const isDuplicate = prev.some(msg =>
          msg.clientMessageId === clientMessageId
        );

        if (isDuplicate) {
          return prev;
        }

        return [...prev, optimisticMessage];
      });

      // Send message if connected
      if (socketRef.current && socketRef.current.readyState === WebSocketClass.OPEN) {
        try {
          socketRef.current.send(JSON.stringify(messageData));
          console.log('[useWebSocketChat] Message sent successfully');
          return true;
        } catch (err) {
          console.error('[useWebSocketChat] Error sending message:', err);

          // Add to queue for retry
          messageQueueRef.current.push({
            ...messageData,
            retries: 0
          });

          return true;
        }
      } else {
        console.log('[useWebSocketChat] WebSocket not connected, queueing message');

        // Add to queue for later
        messageQueueRef.current.push({
          ...messageData,
          retries: 0
        });

        return true;
      }
    } catch (err) {
      console.error('[useWebSocketChat] Error preparing message:', err);
      setError('Failed to send message. Please try again.');
      return false;
    }
  }, [streamId, user, setIsUserTyping, setMessages]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (streamId) {
      // Initialize socketRef if it's not already set
      if (!socketRef.current) {
        console.log('[useWebSocketChat] Initializing socketRef');
        socketRef.current = null;
      }

      connectWebSocket();

      // Set up connection check interval
      const connectionCheckIntervalId = setInterval(() => {
        const ws = socketRef.current;

        // Check if WebSocket is closed or closing
        if (!ws || ws.readyState === WebSocketClass.CLOSED || ws.readyState === WebSocketClass.CLOSING) {
          console.log('[useWebSocketChat] Connection check: WebSocket is closed or closing, reconnecting...');
          connectWebSocket();
        }

        // Check if WebSocket has been inactive for too long
        if (ws && Date.now() - lastActivityRef.current > 60000) {
          console.log('[useWebSocketChat] Connection check: WebSocket inactive for too long, reconnecting...');
          connectWebSocket();
        }
      }, 30000);

      // Set loading to false after a short delay
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      return () => {
        // Clean up
        if (socketRef.current) {
          socketRef.current.close();
        }

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        clearInterval(connectionCheckIntervalId);
      };
    }
  }, [streamId, connectWebSocket]);

  // Process any deferred messages when user is done typing
  useEffect(() => {
    if (!isUserTyping) {
      // Check if we have any deferred messages
      const deferredMessages = messageQueueRef.current.filter(msg => msg.type === 'deferred_message');

      if (deferredMessages.length > 0) {
        console.log(`[useWebSocketChat] Processing ${deferredMessages.length} deferred messages`);

        // Add all deferred messages to state
        setMessages(prev => {
          const newMessages = [...prev];

          for (const deferredMsg of deferredMessages) {
            // Check if we already have this message
            const isDuplicate = newMessages.some(msg =>
              (deferredMsg.data.id && msg.id === deferredMsg.data.id) ||
              (deferredMsg.data._id && msg._id === deferredMsg.data._id) ||
              (deferredMsg.data.clientMessageId && msg.clientMessageId === deferredMsg.data.clientMessageId)
            );

            if (!isDuplicate) {
              newMessages.push(deferredMsg.data);
            }
          }

          return newMessages;
        });

        // Remove processed messages from queue
        messageQueueRef.current = messageQueueRef.current.filter(msg => msg.type !== 'deferred_message');
      }
    }
  }, [isUserTyping]);

  return {
    messages,
    error,
    isLoading,
    isConnected,
    sendMessage,
    socketRef,
    isUserTyping,
    setIsUserTyping // Expose this so the chat input can update it
  };
};

export default useWebSocketChat;
